
import { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { formatDateDisplay, formatTimeOfDay, formatDateTimeInBusinessTZ } from '../../utils/date';
import { trackEvent } from '../../utils/analytics';
import { meetingModeLabel, normalizeMeetingMode } from '../../utils/signupSlots';
import { SignupCalendar } from '../components/SignupCalendar';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Users, 
  Mail, 
  Phone, 
  FileText, 
  Trash2,
  Edit,
  ExternalLink,
  Video
} from 'lucide-react';

interface SignupEvent {
  id: string;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  slot_duration_minutes: number;
  max_signups_per_slot: number;
  is_active: boolean;
  created_at: string;
}

interface SignupRegistration {
  id: string;
  slot_id: string;
  parent_name: string;
  parent_email: string;
  student_name: string;
  phone: string | null;
  notes: string | null;
  meeting_type: string;
  created_at: string;
  student_id: string | null;
  parent_id: string | null;
  signup_slots: {
    id: string;
    slot_date: string;
    start_time: string;
    end_time: string;
    meeting_mode: string | null;
    is_available: boolean;
  };
  students?: {
    id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string | null;
  };
  parents?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
}

function sortRegistrationsBySlotAsc(registrations: SignupRegistration[]): SignupRegistration[] {
  return [...registrations].sort((a, b) => {
    const dateCompare = a.signup_slots.slot_date.localeCompare(b.signup_slots.slot_date);
    if (dateCompare !== 0) return dateCompare;

    const startTimeCompare = a.signup_slots.start_time.localeCompare(b.signup_slots.start_time);
    if (startTimeCompare !== 0) return startTimeCompare;

    return a.created_at.localeCompare(b.created_at);
  });
}

export function AdminSignupDetails() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);


  // Fetch event details
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['signup-event', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('No event ID');
      const { data, error } = await supabase
        .from('signup_events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      return data as SignupEvent;
    },
    enabled: !!eventId,
  });

  // Fetch all registrations for this event
  const { data: registrations = [], isLoading: registrationsLoading } = useQuery({
    queryKey: ['signup-registrations', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('No event ID');
      console.log('Step 1: Getting slots for event:', eventId);
      
      // First get all slots for this event
      const { data: slots, error: slotsError } = await supabase
        .from('signup_slots')
        .select('id')
        .eq('event_id', eventId);
      
      if (slotsError) {
        console.error('Error getting slots:', slotsError);
        throw slotsError;
      }
      
      console.log('Step 2: Found slots:', slots?.length || 0);
      
      if (!slots || slots.length === 0) {
        console.log('No slots found for event');
        return [];
      }
      
      const slotIds = slots.map(s => s.id);
      console.log('Step 3: Looking for registrations in slot IDs:', slotIds.slice(0, 3), '...');
      
      // Then get registrations for those slots (simpler query without complex joins)
      const { data: regs, error: regsError } = await supabase
        .from('signup_registrations')
        .select('*')
        .in('slot_id', slotIds)
        .order('created_at', { ascending: false });
      
      if (regsError) {
        console.error('Error getting registrations:', regsError);
        throw regsError;
      }
      
      console.log('Step 4: Found registrations:', regs?.length || 0);
      
      // If no registrations found through normal query, use the admin function that bypasses RLS
      if (!regs || regs.length === 0) {
        console.log('No registrations found through normal query, trying admin function...');
        
        const { data: adminRegs, error: adminError } = await supabase.rpc('get_event_registrations', {
          p_event_id: eventId
        });
        
        if (adminError) {
          console.error('Admin function failed:', adminError);
          return [];
        }
        
        console.log('Admin function found registrations:', adminRegs?.length || 0);
        
        if (adminRegs && adminRegs.length > 0) {
          // Transform the function result to match our expected format
          const transformedRegs = await Promise.all(
            adminRegs.map(async (reg: Record<string, unknown>) => {
              // Get student info (if available)
              let studentData = null;
              if (reg.student_id) {
                const { data } = await supabase
                  .from('students')
                  .select('id, first_name, last_name, date_of_birth')
                  .eq('id', reg.student_id)
                  .single();
                studentData = data;
              }
              
              // Get parent info (if available)
              let parentData = null;
              if (reg.parent_id) {
                const { data } = await supabase
                  .from('parents')
                  .select('id, first_name, last_name, email, phone')
                  .eq('id', reg.parent_id)
                  .single();
                parentData = data;
              }
              
              return {
                ...reg,
                signup_slots: {
                  id: reg.slot_id,
                  slot_date: reg.slot_date,
                  start_time: reg.start_time,
                  end_time: reg.end_time,
                  meeting_mode: reg.meeting_mode,
                  is_available: reg.is_available,
                },
                students: studentData,
                parents: parentData,
              };
            })
          );
          
          return sortRegistrationsBySlotAsc(transformedRegs as SignupRegistration[]);
        }
      }
      
      if (!regs || regs.length === 0) {
        return [];
      }
      
      // Now enrich each registration with slot, student, and parent data
      const enrichedRegistrations = await Promise.all(
        regs.map(async (reg) => {
          // Get slot info
          const { data: slotData } = await supabase
            .from('signup_slots')
            .select('*')
            .eq('id', reg.slot_id)
            .single();
          
          // Get student info (if available)
          let studentData = null;
          if (reg.student_id) {
            const { data } = await supabase
              .from('students')
              .select('id, first_name, last_name, date_of_birth')
              .eq('id', reg.student_id)
              .single();
            studentData = data;
          }
          
          // Get parent info (if available)
          let parentData = null;
          if (reg.parent_id) {
            const { data } = await supabase
              .from('parents')
              .select('id, first_name, last_name, email, phone')
              .eq('id', reg.parent_id)
              .single();
            parentData = data;
          }
          
          return {
            ...reg,
            signup_slots: slotData,
            students: studentData,
            parents: parentData,
          };
        })
      );
      
      return sortRegistrationsBySlotAsc(enrichedRegistrations as SignupRegistration[]);
    },
    enabled: !!eventId,
  });

  // Delete registration mutation
  const deleteRegistrationMutation = useMutation({
    mutationFn: async (registrationId: string) => {
      const { error } = await supabase
        .from('signup_registrations')
        .delete()
        .eq('id', registrationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-registrations', eventId] });
      queryClient.invalidateQueries({ queryKey: ['signup-slots', eventId] });
      trackEvent({
        eventType: 'admin_signup_registration_delete',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_registrations'
      });
    },
  });

  const handleDeleteRegistration = async (registrationId: string, registrationInfo: string) => {
    if (window.confirm(`Are you sure you want to delete the registration for ${registrationInfo}?`)) {
      await deleteRegistrationMutation.mutateAsync(registrationId);
    }
  };

  const generatePublicLink = (eventId: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup/${eventId}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
  };

  if (!eventId) {
    return <Navigate to="/admin-app/signups" replace />;
  }

  if (eventLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout>
        <div className="text-center py-8">
          <p className="text-gray-500">Event not found.</p>
          <button
            onClick={() => navigate('/admin-app/signups')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Meeting Signups
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* The desktop shell is h-screen/overflow-hidden and hands each page a flex child, so a
          page that does not declare its own scroll container simply overflows and cannot be
          scrolled. Unprefixed rather than `lg:` on purpose: the shell switches to its desktop
          layout at 768px but `lg:` starts at 1024, which would leave a dead band in between
          where nothing scrolls. Matches AdminOverview. */}
      <div className="space-y-6 flex-1 min-h-0 overflow-y-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin-app/signups')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Meeting Signups
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
              <p className="text-gray-600">Registration Details</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => navigate(`/admin-app/signups`)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Manage Event
            </button>
            <button
              onClick={() => copyToClipboard(generatePublicLink(event.id))}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Copy Public Link
            </button>
          </div>
        </div>

        {/* Event Summary */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Event Summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{registrations.length}</div>
                <div className="text-sm text-blue-800">Total Registrations</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {event.is_active ? 'Active' : 'Inactive'}
                </div>
                <div className="text-sm text-green-800">Status</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{event.slot_duration_minutes}min</div>
                <div className="text-sm text-purple-800">Slot Duration</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {formatDateDisplay(event.start_date)} - {formatDateDisplay(event.end_date)}
                </div>
                <div className="text-sm text-orange-800">Date Range</div>
              </div>
            </div>
          </div>
        </div>

        {/* Registrations List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900">
                Registrations ({registrations.length})
              </h2>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            {registrationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : registrations.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No registrations yet</p>
                <p className="text-sm text-gray-400">
                  Share the public link to start receiving signups
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((registration) => (
                  <div
                    key={registration.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1 space-y-3">
                        {/* Registration Info */}
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <Users className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="font-medium text-gray-900">
                                  {registration.parents ? 
                                    `${registration.parents.first_name} ${registration.parents.last_name}` : 
                                    registration.parent_name}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <FileText className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-gray-600">
                                  {registration.students ? 
                                    `${registration.students.first_name} ${registration.students.last_name}` : 
                                    registration.student_name}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <Mail className="h-4 w-4 mr-1" />
                                {registration.parents?.email || registration.parent_email}
                              </div>
                              {(registration.parents?.phone || registration.phone) && (
                                <div className="flex items-center">
                                  <Phone className="h-4 w-4 mr-1" />
                                  {registration.parents?.phone || registration.phone}
                                </div>
                              )}
                            </div>
                            {registration.notes && (
                              <p className="text-sm text-gray-600 italic">
                                Note: {registration.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Slot Info */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-4">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="font-medium">
                                  {formatDateDisplay(registration.signup_slots.slot_date)}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                <span>
                                  {formatTimeOfDay(registration.signup_slots.start_time, { hour12: true })} - {formatTimeOfDay(registration.signup_slots.end_time, { hour12: true })}
                                </span>
                              </div>
                              {normalizeMeetingMode(registration.signup_slots.meeting_mode) && (
                                <div className="flex items-center">
                                  <Video className="h-4 w-4 text-gray-400 mr-2" />
                                  <span>{meetingModeLabel(registration.signup_slots.meeting_mode)}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              Registered: {formatDateTimeInBusinessTZ(registration.created_at, { hour12: true })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="sm:ml-4">
                        <button
                          onClick={() => handleDeleteRegistration(
                            registration.id,
                            `${registration.parent_name} (${registration.student_name})`
                          )}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                          title="Delete registration"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Calendar View */}
        <div className="bg-white shadow rounded-lg mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Calendar View</h2>
            <p className="text-gray-600 mt-1">Visual representation of event slots and registrations</p>
          </div>
          <div className="p-6">
            <SignupCalendar eventId={eventId} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
